import React, { useEffect } from 'react';
import {
  Form,
  FormInstance,
  DateSerialized,
  SelectInput,
  SelectBooleanInput
} from 'blueprint-form';
import { Row, Col } from '../../../../grid';
import { StartDate } from '../date';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectLanguage from '../language';
import SelectTime from '../time';
import Weekday from '../weekday';
import Timezone from '../timezone';
import { CardBlock, CardBreakout } from '../../card';
import Callout from '../../callout';
import {
  GetCurrentTZ,
  GetFormalDate,
  ShorthandDate
} from '../../../../libs/time';
import { useTerms } from '../../../../contexts/terms';

const Modes = [
  {
    label: '1 Reminder per Week',
    value: 'W'
  },
  {
    label: '1 Reminder 2 Days per Week',
    value: 'B'
  },
  {
    label: '1 Reminder per Day',
    value: 'D'
  },
  {
    label: '2 Reminders per Day',
    value: 'P'
  },
  {
    label: '3 Reminders per Day',
    value: 'T'
  },
  {
    label: '4 Reminders per Day',
    value: 'Q'
  },
  {
    label: 'Custom Days',
    value: 'C'
  }
];

type Mode = 'W' | 'B' | 'D' | 'P' | 'T' | 'Q' | 'C';

interface EpxDiabetesData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  isFixed: boolean;
  askSymptoms: boolean;
  askActionPlan: boolean;
  mode?: Mode;
  times: string[];
  days: number[];
  hasFeedback: boolean;
  shouldRecheck: boolean;
  shouldAlertHighAvg: boolean;
  isCheckupEnabled: boolean;
  shouldConfirmBloodSugar: boolean;
  shouldAskMeds?: boolean;
  suppliesFollowUp: boolean;
}

type EpxDiabetesDataRaw = DateSerialized<EpxDiabetesData, 'start' | 'end'>;

const INITIAL_TIMES = ['09:00', '12:00', '18:00', '21:00'];

export const EpxDiabetesDefaultData: EpxDiabetesData = {
  method: 'message',
  language: 'en',
  start: GetFormalDate(ShorthandDate.Today),
  end: null,
  tz: GetCurrentTZ(),
  isFixed: false,
  askSymptoms: false,
  askActionPlan: false,
  mode: 'B',
  times: INITIAL_TIMES,
  days: [1, 4],
  hasFeedback: true,
  shouldRecheck: false,
  shouldAlertHighAvg: false,
  isCheckupEnabled: false,
  shouldConfirmBloodSugar: false,
  shouldAskMeds: false,
  suppliesFollowUp: false
};

const DATE_FORMAT = 'YYYY-MM-DD';
const { DeserializeDate, SerializeDate } = FormInstance;

interface EpxDiabetesFormProps {
  setForm?: (form: FormInstance<EpxDiabetesDataRaw>) => void;
  onSubmit?: (data: EpxDiabetesData) => void;
  isDisabled?: boolean;
}

const GetNTimesFromMode = (mode: Mode): number => {
  let nTimes = 1;
  switch (mode) {
    case 'W':
    case 'B':
    case 'D':
    case 'C':
      nTimes = 1;
      break;
    case 'P':
      nTimes = 2;
      break;
    case 'T':
      nTimes = 3;
      break;
    case 'Q':
      nTimes = 4;
      break;
  }
  return nTimes;
};

const EpxDiabetesForm = ({
  setForm,
  onSubmit: _onSubmit,
  isDisabled
}: EpxDiabetesFormProps): JSX.Element => {
  const { terms } = useTerms();
  const serialize = (data: EpxDiabetesDataRaw): EpxDiabetesData => {
    return {
      ...data,
      start: SerializeDate(data.start, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT),
      times: data.times.slice(0, GetNTimesFromMode(data.mode))
    };
  };
  const deserialize = (data: EpxDiabetesData): EpxDiabetesDataRaw => {
    return {
      ...data,
      start: DeserializeDate(data.start, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT)
    };
  };
  const defaultData = deserialize(EpxDiabetesDefaultData);
  const form = new FormInstance<EpxDiabetesDataRaw, EpxDiabetesData>(
    defaultData,
    { serialize, deserialize }
  );
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const onChange = (data: EpxDiabetesData): void => {
    if (!data.isFixed && data.mode !== 'B') {
      data.mode = 'B';
      form.setData(data);
    }
  };
  const onSubmit = (data: EpxDiabetesData): void => {
    _onSubmit(data);
  };
  return (
    <CardBlock bare>
      <Form
        form={form}
        onChange={onChange}
        onSubmit={onSubmit}
        disabled={isDisabled}
      >
        {(props) => {
          const isFixed = props.values.isFixed;
          const mode = props.values.mode;
          const nTimes = GetNTimesFromMode(mode);
          const timeCols = {
            sm: nTimes > 2 ? 24 : Math.min(24 / nTimes, 12),
            md: Math.min(24 / nTimes, 12)
          };
          let nDays = null;
          if (mode === 'B') {
            nDays = 2;
          } else if (mode === 'W') {
            nDays = 1;
          }
          return (
            <>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectMethod />
                  </Col>
                  <Col sm={12}>
                    <SelectLanguage />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Schedule Type"
                      name="isFixed"
                      trueLabel="Fixed Schedule"
                      falseLabel="SMART Schedule"
                      fill
                    />
                  </Col>
                  {isFixed ? (
                    <Col sm={12}>
                      <SelectInput
                        label="Reminder Frequency"
                        name="mode"
                        options={Modes}
                        fill
                      />
                    </Col>
                  ) : null}
                </Row>
              </CardBlock>
              {['W', 'B', 'C', 'P', 'T', 'Q'].includes(mode) ? (
                <CardBreakout>
                  <Weekday
                    name="days"
                    numItems={nDays}
                    minItems={nDays === null ? 1 : null}
                    inline
                  />
                </CardBreakout>
              ) : null}
              <CardBlock>
                <Row>
                  <Col {...timeCols}>
                    <SelectTime
                      label={nTimes === 1 ? 'Reminder At' : '1st Reminder'}
                      name="times[0]"
                      fill
                    />
                  </Col>
                  {['P', 'T', 'Q'].includes(mode) ? (
                    <Col {...timeCols}>
                      <SelectTime
                        label="2nd Reminder"
                        name="times[1]"
                        defaultValue={INITIAL_TIMES[1]}
                        fill
                      />
                    </Col>
                  ) : null}
                  {['T', 'Q'].includes(mode) ? (
                    <Col {...timeCols}>
                      <SelectTime
                        label="3rd Reminder"
                        name="times[2]"
                        defaultValue={INITIAL_TIMES[2]}
                        fill
                      />
                    </Col>
                  ) : null}
                  {mode === 'Q' ? (
                    <Col {...timeCols}>
                      <SelectTime
                        label="4th Reminder"
                        name="times[3]"
                        defaultValue={INITIAL_TIMES[3]}
                        fill
                      />
                    </Col>
                  ) : null}
                </Row>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Give Feedback"
                      name="hasFeedback"
                      fill
                    />
                    <Callout className="mt-4">
                      If giving feedback, patient will get occasional messages
                      about their blood sugar averages based on their responses.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Ask Medication Question"
                      name="shouldAskMeds"
                      trueLabel="Yes"
                      falseLabel="No"
                      fill
                    />
                    <Callout className="mt-4">
                      If yes, the {terms.patient.default} will be asked if they
                      have taken their diabetes medications for the day after
                      reporting their blood sugar.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Ask About Symptoms"
                      name="askSymptoms"
                      fill
                    />
                    <Callout className="mt-4">
                      If yes, the {terms.patient.default} will be asked whether
                      they are feeling weak, sweaty or having trouble thinking
                      in case of a critical blood sugar value.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Ask About Action Plan"
                      name="askActionPlan"
                      fill
                    />
                    <Callout className="mt-4">
                      If yes, the {terms.patient.default} will be asked whether
                      they have an action plan in case of a critical blood sugar
                      value.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Recheck On Critical Values"
                      name="shouldRecheck"
                      fill
                    />
                    <Callout className="mt-4">
                      If yes, when a critical value is reported, the{' '}
                      {terms.patient.default}
                      will be asked to recheck their blood sugar in 15 minutes.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Alert on High 2-Week Average"
                      name="shouldAlertHighAvg"
                      fill
                    />
                    <Callout className="mt-4">
                      If yes, an alert is triggered when the average
                      pre-prandial blood sugar for the past 2 weeks is above the
                      high average threshold.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Use Check Up / Supply Module"
                      name="isCheckupEnabled"
                      fill
                    />
                    <Callout className="mt-4">
                      If yes, ask the patient whether there is a testing supply
                      issue when the patient is unresponsive.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Supplies Follow-Up Message"
                      name="suppliesFollowUp"
                      fill
                    />
                    <Callout className="mt-4">
                      If yes, when the {terms.patient.default} reports not
                      having enough supplies, a follow-up message will be sent
                      asking if they still need assistance before triggering an
                      alert.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Confirm Blood Sugar Entry"
                      name="shouldConfirmBloodSugar"
                      fill
                    />
                    <Callout className="mt-4">
                      If yes, the {terms.patient.default} will be asked to
                      confirm their blood sugar value before it is submitted.
                      This helps prevent accidental mis-entries.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <StartDate label="Start Date" name="start" fill />
                  </Col>
                  <Col sm={12}>
                    <EndDate label="End Date" name="end" />
                  </Col>
                  <Col sm={12}>
                    <Timezone label="Timezone" name="tz" fill />
                  </Col>
                </Row>
              </CardBlock>
            </>
          );
        }}
      </Form>
    </CardBlock>
  );
};

export default EpxDiabetesForm;
